import { CategoryResult } from '../types/CategoryResult';

/**
 * Category Source - dev-only layer for loading categories from JSON file
 *
 * Responsibilities:
 * 1. Load feed/categories.json as static data
 * 2. Cache normalized categories in memory
 * 3. Provide easy replacement for Bitrix API integration
 *
 * No XML parsing, no regex - only JSON loading.
 */
class CategorySource {
  private categories: CategoryResult[] | null = null;
  private loadingPromise: Promise<CategoryResult[]> | null = null;
  private isLoaded = false;

  constructor() {
    // Start loading categories immediately in background
    this.initializeBackgroundLoad();
  }

  /**
   * Initialize background loading of categories
   */
  private initializeBackgroundLoad(): void {
    // Start loading but don't wait for it
    this.getCategories().catch(error => {
      console.error('Background loading of categories failed:', error);
    });
  }

  /**
   * Get all categories from JSON file (cached, async)
   */
  async getCategories(): Promise<CategoryResult[]> {
    // Return cached data if available
    if (this.categories) {
      console.log('[CategorySource] Returning cached categories:', this.categories.length);
      return this.categories;
    }

    // Prevent duplicate loading
    if (this.loadingPromise) {
      console.log('[CategorySource] Loading already in progress, waiting...');
      return this.loadingPromise;
    }

    console.log('[CategorySource] Starting to load categories from JSON...');

    // Load categories from JSON file
    this.loadingPromise = this.loadCategoriesFromJson();
    this.categories = await this.loadingPromise;
    this.isLoaded = true;

    console.log('[CategorySource] Categories loaded:', this.categories.length);
    return this.categories;
  }

  /**
   * Get all categories synchronously
   * Returns empty array if categories are not loaded yet
   */
  getCategoriesSync(): CategoryResult[] {
    if (!this.categories || !this.isLoaded) {
      // Return empty array instead of throwing error
      // This allows UI to work while categories are loading
      console.log('[CategorySource] Categories not loaded yet, returning empty array');
      return [];
    }
    console.log('[CategorySource] Returning', this.categories.length, 'categories synchronously');
    return this.categories;
  }

  /**
   * Preload categories (call during app initialization)
   */
  async preload(): Promise<void> {
    await this.getCategories();
  }

  /**
   * Check if categories are loaded
   */
  isCategoriesLoaded(): boolean {
    return this.isLoaded && this.categories !== null;
  }

  /**
   * Load categories from JSON file and normalize to CategoryResult
   */
  private async loadCategoriesFromJson(): Promise<CategoryResult[]> {
    try {
      // Fetch JSON file (dev-only static data)
      const response = await fetch('/feed/categories.json');
      if (!response.ok) {
        throw new Error(`Failed to load categories JSON: ${response.status} ${response.statusText}`);
      }

      const jsonData = await response.json();

      // Validate JSON structure
      if (!Array.isArray(jsonData)) {
        throw new Error('Invalid categories JSON: expected array');
      }

      // Normalize to CategoryResult format
      const normalizedCategories = jsonData.map(item => this.normalizeCategory(item));

      return normalizedCategories;
    } catch (error) {
      console.error('Failed to load categories from JSON:', error);
      // Return empty array as fallback (dev environment only)
      return [];
    }
  }

  /**
   * Normalize raw JSON item to CategoryResult
   */
  private normalizeCategory(item: any): CategoryResult {
    // Basic validation
    if (!item.id || !item.title) {
      console.warn('Invalid category item:', item);
    }

    return {
      id: String(item.id),
      type: 'category' as const,
      title: String(item.title),
      parentId: item.parentId ? String(item.parentId) : undefined,
      level: typeof item.level === 'number' ? item.level : undefined,
      // Temporary dev-only URL generation
      url: `/category/${item.id}`,
      // No raw data reference needed for JSON source
      raw: undefined,
    };
  }

  /**
   * Clear cache (for testing/dev purposes)
   */
  clearCache(): void {
    this.categories = null;
    this.loadingPromise = null;
    this.isLoaded = false;
  }
}

// Export singleton instance
export const categorySource = new CategorySource();